import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import "./Contact.css";
import { TextField, Button, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const RATE_LIMIT_MS = 10 * 60 * 1000; // 10 minutes between submissions
const MIN_FORM_TIME_MS = 3000;         // must spend at least 3s on page

type Status = "idle" | "success" | "error" | "rate-limited";

const Contact = () => {
  const form = useRef<HTMLFormElement>(null);
  const loadTime = useRef(Date.now());

  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<Status>("idle");

  const isRateLimited = (): boolean => {
    const last = localStorage.getItem("contactLastSubmit");
    return !!last && Date.now() - parseInt(last) < RATE_LIMIT_MS;
  };

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot: silently drop if a bot filled the hidden field
    if (honeypot) return;

    // Timing check: bots submit instantly
    if (Date.now() - loadTime.current < MIN_FORM_TIME_MS) return;

    // Rate limiting
    if (isRateLimited()) {
      setStatus("rate-limited");
      return;
    }

    if (!form.current) return;

    setLoading(true);
    setStatus("idle");

    emailjs
      .sendForm("service_znzurw7", "template_gea5qah", form.current, {
        publicKey: "ABSW80a6M1UnFNvvn",
      })
      .then(() => {
        setStatus("success");
        localStorage.setItem("contactLastSubmit", Date.now().toString());
        form.current?.reset();
      })
      .catch(() => {
        setStatus("error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,207,157,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(255,207,157,0.65)" },
      "&.Mui-focused fieldset": { borderColor: "#FFCF9D", borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255,207,157,0.7)",
      "&.Mui-focused": { color: "#FFCF9D" },
    },
    "& .MuiOutlinedInput-input": { color: "#fff" },
    "& .MuiOutlinedInput-inputMultiline": { color: "#fff" },
  };

  return (
    <div className="contactus">
      <motion.div
        className="contact-wrapper"
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <div className="contact-header">
          <h2 className="contact-title">Contact Us</h2>
          <div className="contact-title-bar" />
        </div>

        <div className="contact-container">
          {/* ── Form ── */}
          <form className="contact-form" ref={form} onSubmit={sendEmail}>
            {/* Honeypot: invisible to humans, bots fill it in */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
            />

            {status === "success" && (
              <div className="contact-alert contact-alert--success">
                Message sent! We'll get back to you as soon as possible.
              </div>
            )}
            {status === "error" && (
              <div className="contact-alert contact-alert--error">
                Something went wrong. Please try again.
              </div>
            )}
            {status === "rate-limited" && (
              <div className="contact-alert contact-alert--error">
                Please wait a few minutes before sending another message.
              </div>
            )}

            <TextField
              required
              label="Name"
              name="name"
              variant="outlined"
              sx={inputSx}
            />
            <TextField
              required
              label="Email"
              name="email"
              type="email"
              variant="outlined"
              sx={inputSx}
            />
            <TextField
              required
              label="Subject"
              name="title"
              variant="outlined"
              sx={inputSx}
            />
            <TextField
              label="Message"
              name="message"
              multiline
              rows={4}
              variant="outlined"
              sx={inputSx}
            />

            <Button
              type="submit"
              variant="outlined"
              disabled={loading || status === "success"}
              className="contact-btn"
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#FFCF9D" }} />
              ) : (
                "Send Message"
              )}
            </Button>
          </form>

          {/* ── Info ── */}
          <div className="contact-info">
            <div className="contact-card">
              <h3 className="contact-card-title">How it works</h3>
              <p className="contact-card-text">
                Fill out your name, email, and a short message. We'll get back
                to you as soon as possible. Make sure your email is correct so
                we can reply!
              </p>
            </div>

            <div className="contact-card">
              <h3 className="contact-card-title">Other ways to reach us</h3>
              <div className="contact-links">
                <a
                  href="https://wa.me/306944575096"
                  target="_blank"
                  rel="noreferrer"
                  className="contact-link"
                >
                  <WhatsAppIcon className="contact-link-icon contact-link-icon--whatsapp"/>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;
